import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { XpressDeliveryOrderDetail, TextTransformType } from '@shared/models';
import { ServiceTypeEnum, XpressDeliveryStatus } from 'app/models';
import { MapCacheService } from '@shared/services/map-cache.service';
import { MessageService } from 'primeng/api';
import { Clipboard } from '@angular/cdk/clipboard';
import { OrderUtils } from 'app/utils/order-utils';

@Component({
	selector: 'booking-details-tab',
	templateUrl: './booking-details-tab.component.html',
	styleUrl: './booking-details-tab.component.scss'
})
export class BookingDetailsTabComponent implements OnChanges {
	@Input() deliveryOrderDetails!: XpressDeliveryOrderDetail;
	@ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
	map!: google.maps.Map;
	mapInitialized: boolean = false;
	mapLoading: boolean = false;
	showMap: boolean = false;
	mapError: boolean = false;
	TextTransformType = TextTransformType;

	private cachedRouteData?: google.maps.DirectionsResult;

	get riderFullName(): string {
		if (!this.deliveryOrderDetails.RiderFirstName &&
			!this.deliveryOrderDetails.RiderLastName) {
			return '';
		}

		return `${this.deliveryOrderDetails.RiderFirstName} ${this.deliveryOrderDetails.RiderLastName}`;
	}

	get serviceText(): string {
		return `${this.deliveryOrderDetails.ServiceTypeText} - ${this.deliveryOrderDetails.VehicleTypeText}`;
	}

	get isConnexBooking(): boolean {
		return this.deliveryOrderDetails?.ServiceTypeId === ServiceTypeEnum.Connex;
	}

	get isCancelledBooking(): boolean {
		return this.deliveryOrderDetails?.StatusId === XpressDeliveryStatus.Canceled;
	}


	constructor(
		private messageService: MessageService,
		private mapCacheService: MapCacheService,
		private clipboard: Clipboard
	) { }

	copyBookingIdToClipboard(): void {
		const bookingId = this.deliveryOrderDetails?.XpressDeliveryOrderId;
		if (!bookingId) {
			return;
		}

		const copied = this.clipboard.copy(String(bookingId));
		if (copied) {
			this.messageService.add({
				severity: 'success',
				summary: 'Copied',
				detail: 'Booking ID copied to clipboard.'
			});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['deliveryOrderDetails']) {
			const handledByCache = this.checkCachedMapState();

			if (!handledByCache && this.showMap && this.deliveryOrderDetails?.Points) {
				setTimeout(() => {
					this.initializeMap();
				}, 100);
			}
		}
	}

	private checkCachedMapState(): boolean {
		if (!this.deliveryOrderDetails?.XpressDeliveryOrderId) {
			return false;
		}

		const orderId = this.deliveryOrderDetails.XpressDeliveryOrderId;

		if (this.mapCacheService.wasMapLoaded(orderId)) {
			this.showMap = true;
			this.cachedRouteData = this.mapCacheService.getRouteData(orderId);
			setTimeout(() => {
				if (this.deliveryOrderDetails?.Points) {
					this.initializeMap();
				}
			}, 100);
			return true;
		} else {
			this.showMap = false;
			this.cachedRouteData = undefined;
			this.mapInitialized = false;
			this.map = null as unknown as google.maps.Map;
			return false;
		}
	}

	loadMap(): void {
		this.showMap = true;
		this.mapError = false;

		if (this.deliveryOrderDetails?.XpressDeliveryOrderId) {
			this.mapCacheService.setMapLoaded(this.deliveryOrderDetails.XpressDeliveryOrderId, true);
		}
		setTimeout(() => {
			if (this.deliveryOrderDetails?.Points) {
				this.initializeMap();
			}
		}, 100);
	}

	retryLoadMap(): void {
		this.mapError = false;
		this.map = null as unknown as google.maps.Map;
		this.mapInitialized = false;
		this.cachedRouteData = undefined;

		if (this.deliveryOrderDetails?.XpressDeliveryOrderId) {
			this.mapCacheService.removeFromCache(this.deliveryOrderDetails.XpressDeliveryOrderId);
		}
		this.loadMap();
	}

	getStatusClass(value: any): string {
		if (!value) {
			return 'na';
		}
		return String(value).toLowerCase().replace(/\s+/g, '-');
	}

	getStatusDisplayText(statusText: string): string {
		return OrderUtils.getStatusDisplayText(statusText);
	}

	private initializeMap(): void {
		if (!this.mapContainer?.nativeElement) {
			this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Map container not found' });
			this.mapError = true;
			this.mapLoading = false;
			return;
		}

		if (!this.deliveryOrderDetails?.Points || this.deliveryOrderDetails.Points.length === 0) {
			this.mapError = true;
			this.mapLoading = false;
			return;
		}

		this.mapLoading = true;
		const points = this.deliveryOrderDetails.Points;

		try {
			if (!this.map) {
				this.map = new google.maps.Map(this.mapContainer.nativeElement, {
					zoom: 15,
					center: { lat: points[0].Lat, lng: points[0].Long },
				});
				this.mapInitialized = true;
			}

			this.displayRouteInMap(points).catch((error) => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error displaying route on map: ' + error });
				this.mapError = true;
				this.mapLoading = false;
			});
		} catch (error) {
			this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to initialize map' });
			this.mapError = true;
			this.mapLoading = false;
		}
	}

	public displayRouteInMap(points: any): Promise<void> {
		if (!this.map || !points || points.length === 0) {
			this.mapLoading = false;
			return new Promise<void>((resolve) => {
				resolve();
			});
		}

		const startPoint = { lat: points[0].Lat, lng: points[0].Long };
		const endPoint = { lat: points[points.length - 1].Lat, lng: points[points.length - 1].Long };

		if (this.cachedRouteData) {
			this.renderRoute(this.cachedRouteData, startPoint, endPoint);
			this.mapLoading = false;
			return Promise.resolve();
		}

		const directionsService = new google.maps.DirectionsService();

		const waypoints = points.slice(1, -1).map((point: any) => ({
			location: new google.maps.LatLng(point.Lat, point.Long),
			stopover: false,
		}));

		const request: google.maps.DirectionsRequest = {
			origin: new google.maps.LatLng(points[0].Lat, points[0].Long),
			destination: new google.maps.LatLng(points[points.length - 1].Lat, points[points.length - 1].Long),
			waypoints,
			travelMode: google.maps.TravelMode.DRIVING,
		};

		return new Promise((resolve, reject) => {
			directionsService.route(request, (result, status) => {
				if (status === google.maps.DirectionsStatus.OK && result) {
					if (this.deliveryOrderDetails?.XpressDeliveryOrderId) {
						this.mapCacheService.setRouteData(this.deliveryOrderDetails.XpressDeliveryOrderId, result);
						this.cachedRouteData = result;
					}

					this.renderRoute(result, startPoint, endPoint);
					this.mapLoading = false;
					resolve();
				} else {
					this.mapLoading = false;
					this.mapError = true;
					this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Directions request failed' });
					reject(`Directions request failed`);
				}
			});
		});
	}
	private renderRoute(
		result: google.maps.DirectionsResult,
		startPoint: { lat: number; lng: number },
		endPoint: { lat: number; lng: number }
	): void {
		new google.maps.Polyline({
			path: result.routes[0].overview_path,
			geodesic: true,
			strokeColor: '#1CBA70',
			strokeOpacity: 1.0,
			strokeWeight: 5,
			map: this.map,
		});
		new google.maps.Marker({
			position: startPoint,
			map: this.map,
			icon: {
				url: '/Content/xpress-app/images/svg/green-ellipse-marker.svg',
				scaledSize: new google.maps.Size(13, 13),
			},
			title: `Start Point`,
		});

		new google.maps.Marker({
			position: endPoint,
			map: this.map,
			icon: {
				url: '/Content/xpress-app/images/svg/green-ellipse-marker.svg',
				scaledSize: new google.maps.Size(13, 13),
			},
			title: `End Point`,
		});

		this.autoZoomToRoute(result.routes[0].bounds);
	}

	private autoZoomToRoute(bounds: google.maps.LatLngBounds): void {
		if (!this.map || !bounds) {
			return;
		}

		this.map.fitBounds(bounds, {
			top: 50,
			right: 50,
			bottom: 50,
			left: 50
		});

		this.map.addListener('zoom_changed', () => {
			const currentZoom = this.map?.getZoom();
			if (currentZoom && currentZoom > 18) {
				this.map.setZoom(18);
			}
		});
	}

}
